import type { RouterConfig } from '@nuxt/schema';
import { nextTick, watch } from 'vue';
import { useNuxtApp, useState } from '#app';
import { getRouteScrollKey, readScrollPosition, shouldKeepScroll, type ScrollPosition } from '~/utils/scrollRestoration';

const READY_STATE_KEY = 'scroll-ready';
const HISTORY_NAV_KEY = 'scroll-history-navigation';
const CARD_ANCHOR_SELECTOR = '.client-card a[href]';

const getAnchorIdFromHref = (href: string | null): string | null => {
  if (!href) return null;
  const clean = href.split('#')[0]?.split('?')[0] ?? href;
  const segments = clean.split('/').filter(Boolean);
  const last = segments[segments.length - 1];
  if (!last) return null;
  return last;
};

const resolveAnchorPosition = (
  anchorId: string,
  anchorOffset: number
): ScrollPosition | null => {
  const anchors = Array.from(document.querySelectorAll<HTMLAnchorElement>(CARD_ANCHOR_SELECTOR));
  for (const anchor of anchors) {
    const id = getAnchorIdFromHref(anchor.getAttribute('href'));
    if (id !== anchorId) continue;
    const card = anchor.closest('.client-card') ?? anchor;
    const rect = card.getBoundingClientRect();
    const top = Math.max(0, rect.top + window.scrollY - anchorOffset);
    return { left: 0, top };
  }
  return null;
};

const applyScrollHeightPlaceholder = (scrollHeight?: number) => {
  if (!scrollHeight || !Number.isFinite(scrollHeight)) return false;
  const height = Math.max(scrollHeight, document.documentElement.clientHeight);
  document.documentElement.style.minHeight = `${height}px`;
  if (document.body) {
    document.body.style.minHeight = `${height}px`;
  }
  return true;
};

const clearScrollHeightPlaceholder = () => {
  document.documentElement.style.minHeight = '';
  if (document.body) {
    document.body.style.minHeight = '';
  }
};

const waitForScrollReady = (key: string) => {
  const readyState = useState<Record<string, boolean>>(READY_STATE_KEY, () => ({}));
  if (readyState.value[key]) return Promise.resolve();

  return new Promise<void>((resolve) => {
    const stop = watch(
      () => readyState.value[key],
      (ready) => {
        if (ready) {
          stop();
          resolve();
        }
      }
    );
  });
};

export default <RouterConfig>{
  scrollBehavior(to, from, savedPosition) {
    if (!process.client) return;

    const keepScroll = shouldKeepScroll(to);
    const historyNavigation = useState<boolean>(HISTORY_NAV_KEY, () => false);
    const state = window.history.state as { forward?: string | null } | null;
    const hasSavedPosition = savedPosition !== null && savedPosition !== undefined;
    const shouldRestore =
      keepScroll &&
      (hasSavedPosition || historyNavigation.value || Boolean(state?.forward));
    const storedPosition = shouldRestore ? readScrollPosition(to) : null;
    const position = shouldRestore ? (savedPosition ?? storedPosition) : null;
    const placeholderApplied = keepScroll ? applyScrollHeightPlaceholder(storedPosition?.scrollHeight) : false;

    if (keepScroll && position) {
      const nuxtApp = useNuxtApp();

      if (to.meta?.scrollRestoration) {
        // Scroll immediately to establish position
        window.scrollTo({
          left: position.left ?? 0,
          top: position.top ?? 0,
          behavior: 'instant',
        });

        // Refine position after content is ready
        nuxtApp.hooks.hookOnce('page:finish', async () => {
          await waitForScrollReady(getRouteScrollKey(to));
          await nextTick();

          const anchorPosition =
            storedPosition?.anchorId && typeof storedPosition.anchorOffset === 'number'
              ? resolveAnchorPosition(storedPosition.anchorId, storedPosition.anchorOffset)
              : null;
          const finalPosition = anchorPosition ?? position;

          // Final scroll to refined position
          window.scrollTo({
            left: finalPosition.left ?? 0,
            top: finalPosition.top ?? 0,
            behavior: 'instant',
          });

          if (placeholderApplied) {
            clearScrollHeightPlaceholder();
          }
        });
      } else {
        // For pages without scrollRestoration meta, scroll immediately
        window.scrollTo({
          left: position.left ?? 0,
          top: position.top ?? 0,
          behavior: 'instant',
        });

        if (placeholderApplied) {
          nuxtApp.hooks.hookOnce('page:finish', () => {
            clearScrollHeightPlaceholder();
          });
        }
      }

      return position;
    }

    if (to.hash) {
      const isSamePage = to.path === from.path;
      if (isSamePage) {
        return {
          el: to.hash,
          behavior: 'smooth',
        };
      }

      return new Promise((resolve) => {
        const nuxtApp = useNuxtApp();
        nuxtApp.hooks.hookOnce('page:finish', async () => {
          await nextTick();
          resolve({
            el: to.hash,
            behavior: 'smooth',
          });
        });
      });
    }

    return false;
  },
};
